/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file notificationChannels.js
 * @description
 * Android notification channels + channel groups for the whole app, derived
 * from the shared {@link notificationCatalog} categories so the system
 * notification settings mirror the in-app preference panel 1:1.
 *
 * - One channel PER CATALOG CATEGORY (id === category id, e.g. 'mail',
 *   'gastro', 'sysops'), organized under three channel groups (communication /
 *   operations / system) in the OS settings screen.
 * - Channel + group names are LOCALIZED via the existing `notif_cat_*` keys
 *   and re-applied on every i18n language change (Android updates a channel's
 *   visible name in place when it is re-created with the same id).
 * - The legacy 'default' channel is kept as the backward-compatible target of
 *   older backend pushes (and as the FCM manifest fallback channel).
 *
 * The backend groups its FCM pushes with the SAME category ids
 * (`android.notification.channelId` + `group`, `apns thread-id`), so remote
 * and local notifications bundle together per category in the tray.
 */

import * as Notifications from 'expo-notifications';
import {Platform} from 'react-native';
import i18n from '../../i18n';
import {CATEGORIES, NOTIFICATION_EVENTS} from './notificationCatalog';

/** Android channel-group ids → member category ids + localized name key. */
export const CHANNEL_GROUPS = [
    {
        id: 'communication',
        nameKey: 'notif_channel_group_communication',
        defaultName: 'Kommunikáció',
        categories: ['common', 'mail', 'chat', 'support', 'voip'],
    },
    {
        id: 'operations',
        nameKey: 'notif_channel_group_operations',
        defaultName: 'Működés és értékesítés',
        categories: ['gastro', 'driver', 'service', 'crm', 'billing', 'warehouse', 'records'],
    },
    {
        id: 'system',
        nameKey: 'notif_channel_group_system',
        defaultName: 'Rendszer',
        categories: ['system', 'sysops'],
    },
];

/** Categories whose alerts must heads-up (peek) by default. */
const HIGH_IMPORTANCE_CATEGORIES = new Set(['common', 'mail', 'chat', 'gastro', 'driver', 'service', 'support', 'voip']);

const categoryToGroupId = CHANNEL_GROUPS.reduce((acc, group) => {
    group.categories.forEach((categoryId) => {
        acc[categoryId] = group.id;
    });
    return acc;
}, {});

/** Exact eventKey → category from the shared catalog (single source of truth). */
const EVENT_KEY_TO_CATEGORY = NOTIFICATION_EVENTS.reduce((acc, event) => {
    acc[String(event.key).toLowerCase()] = event.category;
    return acc;
}, {});

/** Prefix fallback for eventKeys NOT in the catalog, e.g. 'meeting.ready' → 'common'. */
const EVENT_PREFIX_TO_CATEGORY = {
    assignment: 'common', mention: 'common', comment: 'common', task: 'common',
    project: 'common', calendar: 'common', meeting: 'common',
    mail: 'mail', email: 'mail',
    order: 'gastro', reservation: 'gastro', guest: 'gastro', booking: 'gastro',
    ticket: 'service', appointment: 'service', workorder: 'service',
    lead: 'crm', customer: 'crm', complaint: 'crm', website: 'crm',
    quote: 'billing', invoice: 'billing', payment: 'billing', procurement: 'billing',
    card: 'billing', billing: 'billing',
    inventory: 'warehouse',
    support: 'support',
    // Team Chat: `type: 'chat' | 'chat-meeting'` and `chat.*` eventKeys. NB the chat
    // push's `data.channelId` is the CHAT channel id, never an Android channel.
    chat: 'chat',
    voip: 'voip',
    // Driver app: `type: 'driver'` and `driver.*` eventKeys → the 'driver' channel.
    driver: 'driver',
    fleet: 'records', haccp: 'records',
    system: 'system', integration: 'system', app: 'system', user: 'system', security: 'system',
    sysops: 'sysops',
};

/**
 * Resolve the Android channel id a push should land on.
 * Preference order: explicit `data.channelId` from the backend → catalog
 * category derived from `data.eventKey`/`data.type` → legacy 'default'.
 *
 * @param {Object} [data] FCM/local notification data payload
 * @returns {string} an existing channel id (always one this module creates)
 */
export const resolveChannelIdForPushData = (data = {}) => {
    const explicit = String(data.channelId || '').trim();
    if (explicit && (explicit === 'default' || categoryToGroupId[explicit])) {
        return explicit;
    }

    const eventKey = String(data.eventKey || data.type || '').trim().toLowerCase();
    if (!eventKey) {
        return 'default';
    }
    if (categoryToGroupId[eventKey]) {
        return eventKey; // already a category id (e.g. type: 'mail')
    }
    if (EVENT_KEY_TO_CATEGORY[eventKey]) {
        return EVENT_KEY_TO_CATEGORY[eventKey];
    }
    const prefix = eventKey.split(/[.:_-]/)[0];

    return EVENT_PREFIX_TO_CATEGORY[prefix] || 'default';
};

/**
 * iOS thread identifier for tray bundling — mail bundles per conversation
 * thread, everything else per category (mirrors the backend's `thread-id`).
 *
 * @param {Object} [data] notification data payload
 * @returns {string}
 */
export const resolveThreadIdentifierForPushData = (data = {}) => {
    const category = resolveChannelIdForPushData(data);
    if (category === 'mail' && data.threadKey) {
        return `mail:${data.threadKey}`;
    }

    return category;
};

let lastAppliedLanguage = null;
let applyingPromise = null;

const localizedCategoryName = (category) => i18n.t(category.labelKey, {defaultValue: category.label});

/**
 * Create/refresh every channel group + per-category channel with names in the
 * CURRENT app language. Safe to call repeatedly (Android upserts by id).
 * No-op on iOS/web.
 *
 * @returns {Promise<void>}
 */
export const ensureNotificationChannels = async () => {
    if (Platform.OS !== 'android') {
        return;
    }
    if (typeof Notifications?.setNotificationChannelAsync !== 'function') {
        return;
    }

    // Serialize concurrent callers (bootstrap + language change can race).
    if (applyingPromise) {
        return applyingPromise;
    }

    applyingPromise = (async () => {
        try {
            if (typeof Notifications.setNotificationChannelGroupAsync === 'function') {
                for (const group of CHANNEL_GROUPS) {
                    try {
                        await Notifications.setNotificationChannelGroupAsync(group.id, {
                            name: i18n.t(group.nameKey, {defaultValue: group.defaultName}),
                        });
                    } catch (_e) {
                        // Group creation is best-effort; channels still work ungrouped.
                    }
                }
            }

            for (const category of CATEGORIES) {
                try {
                    await Notifications.setNotificationChannelAsync(category.id, {
                        name: localizedCategoryName(category),
                        groupId: categoryToGroupId[category.id],
                        importance: HIGH_IMPORTANCE_CATEGORIES.has(category.id)
                            ? Notifications.AndroidImportance.HIGH
                            : Notifications.AndroidImportance.DEFAULT,
                        sound: 'default',
                        vibrationPattern: [0, 250, 250, 250],
                        lightColor: '#f1b34b',
                        lockscreenVisibility: Notifications.AndroidNotificationVisibility.PRIVATE,
                    });
                } catch (_e) {
                    // One failing channel must never block the rest.
                }
            }

            // Legacy channel older backend builds still target + FCM manifest fallback.
            try {
                await Notifications.setNotificationChannelAsync('default', {
                    name: i18n.t('notif_channel_default', {defaultValue: 'Általános értesítések'}),
                    importance: Notifications.AndroidImportance.HIGH,
                    sound: 'default',
                    vibrationPattern: [0, 250, 250, 250],
                    lightColor: '#f1b34b',
                    lockscreenVisibility: Notifications.AndroidNotificationVisibility.PRIVATE,
                });
            } catch (_e) {
                // Best-effort.
            }

            lastAppliedLanguage = i18n.language || lastAppliedLanguage;
        } finally {
            applyingPromise = null;
        }
    })();

    return applyingPromise;
};

// Keep channel/group names in the user's app language: re-apply on every
// language switch (Android updates names in place for existing channel ids).
try {
    i18n.on('languageChanged', (language) => {
        if (language && language !== lastAppliedLanguage) {
            ensureNotificationChannels().catch(() => {});
        }
    });
} catch (_e) {
    // i18n emitter unavailable (tests) — channels still localize on next ensure.
}
